import React from 'react';
import styled from 'styled-components';
import { CopyrightCircleOutlined } from '@ant-design/icons';
import Image from 'components/Image';

interface Props {
    image: string;
    copyright?: string;
    caption?: string;
    width?: number | string;
    height?: number | string;
    float?: string;
    alt?: string;
    margin?: string;
    type?: string;
    imageProps?: Record<PropertyKey, string | boolean> & {
        dangerouslySetInnerHTML?:
            | {
                  __html: string;
              }
            | undefined;
        key?: string | number | undefined;
        style?: Record<PropertyKey, string> | undefined;
    };
    priority?: boolean | undefined;
}

interface ContainerProps {
    $float: string;
    $width: string;
    $height: string;
    $margin?: string;
}

const Container = styled.figure<ContainerProps>`
    position: relative;
    margin: ${(props: ContainerProps) => props.$margin || 0};
    float: ${(props: ContainerProps) => props.$float};
`;
const Copyrights = styled.span`
    position: absolute;
    max-width: 100%;
    max-height: 100%;
    background: rgba(0, 0, 0, 0.5);
    bottom: 9px;
    right: 0;
    padding: 5px;
    transform: rotate(180deg);
    writing-mode: vertical-rl;

    font-size: 14px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.21;
    letter-spacing: normal;
    text-align: left;
    color: #fff;
`;

const FigCaption = styled.figcaption`
    background-color: #dddddd;
    margin: -10px 0 20px 0;
    padding: 10px;
    line-height: 1.5;
    font-size: 14px;
`;

const ArticleImage = ({
    image,
    copyright,
    alt,
    margin,
    caption,
    float = 'center',
    height = '100%',
    width = '100%',
    type = 'index',
    imageProps,
    ...restProps
}: Props): JSX.Element => {
    const parseDimensionToNumber = (value: string): number => {
        if (value.endsWith('%')) {
            return Math.round(885 * (parseInt(value.replace('%', ''), 10) / 100));
        }
        if (value.endsWith('px')) {
            return parseInt(value.replace('px', ''), 10);
        }

        return parseInt(value, 10);
    };

    const { style: imageStyle, ...restImageProps } = imageProps || { style: {} };
    return (
        <Container
            $float={float}
            $height={isNaN(+height) ? String(height) : `${height}px`}
            $width={isNaN(+width) ? String(height) : `${width}px`}
            $margin={margin}
            {...restProps}
        >
            <Image
                type={type}
                alt={alt}
                src={image}
                width={typeof width === 'string' ? parseDimensionToNumber(width) : width || 800}
                height={typeof height === 'string' ? parseDimensionToNumber(height) : height || 450}
                {...restImageProps}
                style={{
                    maxWidth: '100%',
                    objectFit: 'contain',
                    height: 'auto',
                    ...imageStyle
                }}
            />
            {caption && <FigCaption>{caption}</FigCaption>}
            {copyright && (
                <Copyrights>
                    <CopyrightCircleOutlined rotate={90} size={14} style={{ verticalAlign: 0 }} />{' '}
                    {copyright}
                </Copyrights>
            )}
        </Container>
    );
};

export default ArticleImage;
